import { BookmarkIcon as BookmarkOutline } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolid } from '@heroicons/react/24/solid';
import React from 'react';
import { usePinnedResources } from '../hooks/usePinnedResources';
import type { ResourceEntryKind } from '../utils/resourceIndex';

interface PinButtonProps {
    kind: ResourceEntryKind;
    id: number;
    /** Used in the accessible label so screen readers name the target. */
    title: string;
    className?: string;
}

/**
 * Pins a resource to the overview page. Personal and local to the browser —
 * see {@link usePinnedResources} for why this is not server state.
 */
export const PinButton: React.FC<PinButtonProps> = ({ kind, id, title, className = '' }) => {
    const { isPinned, toggle } = usePinnedResources();
    const pinned = isPinned(kind, id);
    const Icon = pinned ? BookmarkSolid : BookmarkOutline;

    return (
        <button
            type="button"
            onClick={(event) => {
                // Rows are links; pinning must not navigate.
                event.preventDefault();
                event.stopPropagation();
                toggle(kind, id);
            }}
            aria-pressed={pinned}
            aria-label={pinned ? `Unpin ${title}` : `Pin ${title}`}
            title={pinned ? 'Unpin' : 'Pin to your overview'}
            className={`flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors ${className}`}
        >
            <Icon className={`w-4 h-4 ${pinned ? 'text-gray-800' : 'text-gray-400'}`} />
        </button>
    );
};

export default PinButton;
